// token-refresh.interceptor.ts
import { Injectable, Optional } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { MsalAuthService } from './msal-auth.service';
import { LocalStorageService } from 'app/core/mips/common/services/local-storage.service';
import { Router } from '@angular/router';
import { SnackbarService } from 'app/core/mips/common/services/snackbar.service';
import { MatDialog } from '@angular/material/dialog';

export const IS_RETRY_REQUEST = new HttpContextToken<boolean>(() => false);

@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private isSessionExpiredShowing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private msalAuthService: MsalAuthService,
    private router: Router,
    private snackbarService: SnackbarService,
    @Optional() private dialog?: MatDialog
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes('/api/msal-login') || request.context.get(IS_RETRY_REQUEST) || request.headers.has('X-Is-Retry')) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse) {
          const detail = typeof error.error?.detail === 'string' ? error.error.detail.toLowerCase() : '';
          const isAuthOrTokenError = error.status === 401 ||
            (error.status === 403 && (
              detail.includes('token') ||
              detail.includes('signature') ||
              detail.includes('username') ||
              detail.includes('credential') ||
              detail.includes('authentication')
            ));

          if (isAuthOrTokenError) {
            return this.handle401Error(request, next);
          }
        }

        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const currentToken = LocalStorageService.getLocalStorageValueBasedOnKeys('MSAL_JWT_TOKEN');
    const authHeader = request.headers.get('Authorization');

    // If another concurrent request has already refreshed the token in localStorage,
    // retry immediately with the fresh token without initiating another refresh cycle.
    if (currentToken && authHeader && authHeader !== `Bearer ${currentToken}`) {
      return next.handle(this.addToken(request, currentToken));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // Acquire a fresh token (MsalAuthService handles deduplication of concurrent requests)
      return this.msalAuthService.refreshToken(true).pipe(
        catchError((err) => {
          this.isRefreshing = false;
          return this.handleRefreshFailure(err);
        }),
        switchMap(token => {
          this.isRefreshing = false;

          if (token) {
            this.refreshTokenSubject.next(token);
            LocalStorageService.setLocalStorageValueBasedOnKey('MSAL_JWT_TOKEN', token);
            LocalStorageService.setLocalStorageValueBasedOnKey('API_KEY', token);
            return next.handle(this.addToken(request, token));
          } else {
            return this.handleRefreshFailure(new Error('Silent token refresh failed'));
          }
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          if (!token || token === '') {
            return throwError(() => new Error('Queued token refresh failed'));
          }
          return next.handle(this.addToken(request, token));
        })
      );
    }
  }

  private handleRefreshFailure(err: any): Observable<never> {
    this.refreshTokenSubject.next('');
    this.handleSessionExpired('Your session has expired. Please sign in again.');
    return throwError(() => err);
  }

  private handleSessionExpired(message: string): void {
    if (this.isSessionExpiredShowing || this.router.url.includes('/login')) {
      return;
    }
    this.isSessionExpiredShowing = true;

    try {
      this.dialog?.closeAll();
      this.msalAuthService.clearAuthenticationState();
      alert(message);
    } finally {
      this.router.navigate(['/login'], { replaceUrl: true }).then(() => {
        this.isSessionExpiredShowing = false;
      }).catch(() => {
        this.isSessionExpiredShowing = false;
      });
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      context: request.context.set(IS_RETRY_REQUEST, true),
      setHeaders: {
        'Authorization': `Bearer ${token}`
      }
    });
  }
}
